import { useEffect, useRef } from "react";
import { useTheme } from "@/contexts/ThemeContext";

/**
 * MatrixBackground — site-wide watermark.
 * The grid drifts slowly downward (matrix rain) but individual cells also
 * randomly shimmer and mutate their text. Drift wraps over the full grid
 * span so motion is continuous (no per-row snap/reset).
 */
/* Short "data atoms" — single characters, 2-3 char tickers and direction
   markers. Keeping each atom tiny means every grid cell can be the same
   small square size, producing the classic Matrix-rain even-spaced look
   instead of horizontal stripes. */
const SYMBOLS = [
  // FX 2-3 char roots
  "EU", "GB", "JP", "US", "CA", "AU", "NZ", "CH",
  "TR", "MX", "ZA", "SG", "HK", "CN", "NO", "SE", "DK", "PL", "HU",
  "EUR", "USD", "GBP", "JPY", "AUD", "CAD", "CHF", "NZD",
  // FX 3-char tickers / single letters
  "FX", "XAU", "XAG", "OIL", "GAS",
  "BTC", "ETH", "XRP", "LTC", "ADA", "SOL", "DOT", "AVA", "DOG", "BNB", "MAT",
  "SPX", "NAS", "DJI", "DAX", "FTS", "JPN", "HSI", "VIX", "DXY",
  "BAC", "WFC", "DIS",
  "APL", "MSF", "NVD", "AMZ", "MTA", "TSL",
  // Status atoms (3 chars only — 2-char items like "AA"/"BA" read as dots)
  "BUY", "SEL", "BID", "ASK", "TKY", "LDN", "NYC", "SYD",
];
const PIPS = [
  // 1-3 char tags (no single chars / dots / specials)
  "OK", "ERR", "RUN", "PND", "PRT", "FIL", "REJ", "OPN",
  "P/L", "S/L", "T/P",
  // Small numerics & spreads (≥3 chars so they never read as snow)
  "0.0", "0.1", "0.5", "1.0", "1.5", "2.0", "5.0",
  "+12", "-08", "+45", "-21", "+99", "-32",
  "0.01", "0.05", "0.10", "0.25",
  "12%", "31%", "45%", "92%", "20%",
  // Leverages
  "x50", "100", "200", "500",
  // Status
  "WIN", "RIP", "TOP", "LOW", "MID", "MAX", "MIN",
];
const pickText = () => {
  const arr = Math.random() < 0.5 ? SYMBOLS : PIPS;
  return arr[(Math.random() * arr.length) | 0];
};

export default function MatrixBackground() {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const { theme } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Respect user's motion preference — no animation, but render a
    // single static pass so the watermark still appears.
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ctx = canvas.getContext("2d");
    let w = 0, h = 0;
    let cells = [];
    let yOffset = 0;              // global continuous drift offset
    let span = 0;                 // total vertical span of the grid
    let mouseX = -9999, mouseY = -9999; // last cursor position
    const HALO_R = 180;          // radius around cursor where cells brighten

    const FONT_PX = 11;
    const DRIFT_PX_PER_SEC = 22;  // continuous slow drift, never resets

    // Measure the widest atom in the symbol pool so we can size cells
    // to ALWAYS contain the text with a small safety gap on each side.
    // Guarantees no atom can overlap into an adjacent cell.
    ctx.font = `${FONT_PX}px 'JetBrains Mono', ui-monospace, monospace`;
    let maxAtomWidth = 0;
    for (const arr of [SYMBOLS, PIPS]) {
      for (const txt of arr) {
        const w = ctx.measureText(txt).width;
        if (w > maxAtomWidth) maxAtomWidth = w;
      }
    }
    // 6px total padding (3px each side) ensures a visible inter-cell gap.
    const CELL_SIZE = Math.ceil(maxAtomWidth) + 6;
    const CELL_W = CELL_SIZE;
    const CELL_H = CELL_SIZE;

    const isLight = theme === "light";
    const colorPrefix = isLight ? "rgba(15,60,82," : "rgba(170,222,235,";
    // Visible but subtle.
    const peakAlpha = isLight ? 0.14 : 0.18;
    // Constant baseline so non-shimmering cells are faintly visible too.
    const baseAlpha = isLight ? 0.025 : 0.035;

    const buildGrid = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);

      const cols = Math.ceil(w / CELL_W) + 1;
      // Add 2 extra rows for over-scan so the wrap is invisible.
      const rows = Math.ceil(h / CELL_H) + 2;
      span = rows * CELL_H;
      cells = [];
      for (let r = 0; r < rows; r++) {
        for (let cIdx = 0; cIdx < cols; cIdx++) {
          cells.push({
            x: cIdx * CELL_W,
            y: r * CELL_H,
            txt: pickText(),
            shimmer: 0,
            shimmerDir: 0,
            shimmerTotal: 0,
            shimmerAge: 0,
            mutated: false,
          });
        }
      }
    };
    buildGrid();

    // ~5% of cells shimmering at any moment
    const targetActive = () => Math.max(40, Math.floor(cells.length * 0.05));

    const activate = (cell) => {
      cell.shimmer = 0;
      cell.shimmerDir = 1;
      cell.shimmerAge = 0;
      cell.shimmerTotal = 700 + Math.random() * 1900;
      cell.mutated = false;
      // ~12% chance to become a market tick — green ▲ or red ▼ — for
      // the lifetime of this shimmer. Subtle but consistently produces
      // a "market move" feel woven into the grid.
      const r = Math.random();
      if (r < 0.06) {
        cell.txt = "▲";
        cell.tick = "up";
      } else if (r < 0.12) {
        cell.txt = "▼";
        cell.tick = "dn";
      } else {
        cell.tick = null;
        if (Math.random() < 0.5) cell.txt = pickText();
      }
    };

    for (let i = 0; i < targetActive(); i++) {
      activate(cells[(Math.random() * cells.length) | 0]);
    }

    let lastT = performance.now();

    // Pause rendering when the tab isn't visible — eliminates all
    // background CPU usage when the user switches tabs.
    let paused = document.hidden;
    const onVisChange = () => {
      paused = document.hidden;
      if (!paused) {
        lastT = performance.now();
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    document.addEventListener("visibilitychange", onVisChange);

    const tick = (t) => {
      if (paused) return;
      const dt = Math.min(48, t - lastT);
      lastT = t;
      // Global downward drift — modulo by the FULL grid span so cells
      // continuously cycle from bottom back to the top with no visible
      // snap or reset. (Previously % CELL_H made the grid bounce 18px
      // every ~1.3s, which felt clunky.)
      yOffset = (yOffset + (DRIFT_PX_PER_SEC * dt) / 1000) % span;

      ctx.clearRect(0, 0, w, h);
      ctx.font = `${FONT_PX}px 'JetBrains Mono', ui-monospace, monospace`;
      ctx.textBaseline = "top";
      ctx.textAlign = "center";   // centre each symbol in its cell so variable-length text reads as a uniform grid

      // Pass 1 — faint baseline on every cell so the grid is always
      // faintly visible (the "ambient" look). Cells near the cursor
      // get an additional brightness boost (mouse-reactive halo).
      for (let i = 0; i < cells.length; i++) {
        const c = cells[i];
        let y = c.y + yOffset;
        // Wrap any cell that has drifted below the viewport back to the
        // top so the column reads as an endless stream.
        if (y >= span) y -= span;
        if (y < -CELL_H || y > h) continue;

        const cx = c.x + CELL_W / 2;  // centre of the cell horizontally
        // Distance from cursor for halo boost
        const dx = cx - mouseX;
        const dy = y - mouseY;
        const d2 = dx * dx + dy * dy;
        let a = baseAlpha;
        if (d2 < HALO_R * HALO_R) {
          // Linear falloff to 0 at the radius edge
          const t = 1 - Math.sqrt(d2) / HALO_R;
          a += t * (peakAlpha - baseAlpha);
        }
        ctx.fillStyle = colorPrefix + a.toFixed(3) + ")";
        ctx.fillText(c.txt, cx, y);
      }

      // Pass 2 — bright shimmer overlay on actively-animating cells.
      let active = 0;
      for (let i = 0; i < cells.length; i++) {
        const c = cells[i];
        if (c.shimmerDir === 0) continue;
        active++;
        c.shimmerAge += dt;
        const u = c.shimmerAge / c.shimmerTotal;
        c.shimmer = Math.sin(Math.min(1, u) * Math.PI); // bell curve

        // Mutate value at the brightest point (skip for tick cells so
        // ▲/▼ stay green/red throughout the shimmer life).
        if (!c.mutated && u >= 0.45 && !c.tick) {
          c.txt = pickText();
          c.mutated = true;
        }
        if (u >= 1) {
          c.shimmer = 0;
          c.shimmerDir = 0;
          c.tick = null;
          continue;
        }
        const a = c.shimmer * peakAlpha;
        if (a < 0.003) continue;

        let y = c.y + yOffset;
        if (y >= span) y -= span;
        if (y < -CELL_H || y > h) continue;
        // Ticks render in their market colour (green up / red down).
        if (c.tick === "up") {
          // Scale alpha up slightly so the colour reads clearly.
          const ta = Math.min(0.85, a * 4);
          ctx.fillStyle = `rgba(80, 220, 130, ${ta.toFixed(3)})`;
        } else if (c.tick === "dn") {
          const ta = Math.min(0.85, a * 4);
          ctx.fillStyle = `rgba(240, 90, 90, ${ta.toFixed(3)})`;
        } else {
          ctx.fillStyle = colorPrefix + a.toFixed(3) + ")";
        }
        ctx.fillText(c.txt, c.x + CELL_W / 2, y);
      }

      // Top up to keep targetActive shimmering at all times
      const ta = targetActive();
      let need = ta - active;
      while (need-- > 0) {
        const c = cells[(Math.random() * cells.length) | 0];
        if (c.shimmerDir === 0) activate(c);
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    if (prefersReducedMotion) {
      // Render one static pass only — no animation loop, no rAF.
      // Skip activation so the grid is purely the faint baseline.
      tick(performance.now());
    } else {
      rafRef.current = requestAnimationFrame(tick);
    }

    const onResize = () => buildGrid();
    window.addEventListener("resize", onResize, { passive: true });

    const onMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };
    const onMouseLeave = () => {
      mouseX = -9999;
      mouseY = -9999;
    };
    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("mouseleave", onMouseLeave, { passive: true });

    return () => {
      cancelAnimationFrame(rafRef.current);
      document.removeEventListener("visibilitychange", onVisChange);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [theme]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none"
      style={{
        zIndex: 0,
        maskImage: "linear-gradient(180deg, transparent 0%, #000 6%, #000 94%, transparent 100%)",
        WebkitMaskImage: "linear-gradient(180deg, transparent 0%, #000 6%, #000 94%, transparent 100%)",
      }}
    />
  );
}
